import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Link,
  Divider,
  Alert
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { generateTrialBalance } from '../firebase/trialBalanceServices';
import { generateProfitLoss } from '../firebase/profitLossServices';
import { generateBalanceSheet } from '../firebase/balanceSheetServices';

// Mock data for reports
const monthlyIncome = [
  { month: 'Jan', amount: 5200 },
  { month: 'Feb', amount: 5800 },
  { month: 'Mar', amount: 6100 },
  { month: 'Apr', amount: 7200 },
  { month: 'May', amount: 6800 },
  { month: 'Jun', amount: 7500 },
  { month: 'Jul', amount: 8200 },
  { month: 'Aug', amount: 7800 },
  { month: 'Sep', amount: 8500 },
  { month: 'Oct', amount: 8750 },
  { month: 'Nov', amount: 0 },
  { month: 'Dec', amount: 0 }
];

const monthlyExpenses = [
  { month: 'Jan', amount: 3100 },
  { month: 'Feb', amount: 3400 },
  { month: 'Mar', amount: 3600 },
  { month: 'Apr', amount: 4100 },
  { month: 'May', amount: 3900 },
  { month: 'Jun', amount: 4300 },
  { month: 'Jul', amount: 4600 },
  { month: 'Aug', amount: 4500 },
  { month: 'Sep', amount: 5100 },
  { month: 'Oct', amount: 5320 },
  { month: 'Nov', amount: 0 },
  { month: 'Dec', amount: 0 }
];

const monthlyProfit = monthlyIncome.map((income, index) => ({
  month: income.month,
  amount: income.amount - monthlyExpenses[index].amount
}));

const categoryBreakdown = [
  { category: 'Services', amount: 5600, percentage: 64 },
  { category: 'Products', amount: 2350, percentage: 27 },
  { category: 'Gift Cards', amount: 550, percentage: 6 },
  { category: 'Classes', amount: 250, percentage: 3 }
];

const expensesBreakdown = [
  { category: 'Rent', amount: 1200, percentage: 22 },
  { category: 'Payroll', amount: 3500, percentage: 66 },
  { category: 'Supplies', amount: 458, percentage: 9 },
  { category: 'Utilities', amount: 162, percentage: 3 }
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `report-tab-${index}`,
    'aria-controls': `report-tabpanel-${index}`,
  };
}

// Define interfaces for Trial Balance data
interface TrialBalanceItem {
  accountId: string;
  accountNumber: string;
  accountName: string;
  debitBalance: number;
  creditBalance: number;
  netBalance: number;
}

interface TrialBalanceData {
  items: TrialBalanceItem[];
  totalDebit: number;
  totalCredit: number;
  startDate?: Date;
  endDate?: Date;
  asOfDate: Date;
}

// Format currency helper function
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2
  }).format(amount);
};

const Reports: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState<string>('monthly');
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());
  const [month, setMonth] = useState<string>(new Date().getMonth().toString()); // Use month index (0-11) as string
  const [chartType, setChartType] = useState('bar');
  const [trialBalanceData, setTrialBalanceData] = useState<TrialBalanceData | null>(null);
  const [profitLossData, setProfitLossData] = useState<any>(null);
  const [balanceSheetData, setBalanceSheetData] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTimeRangeChange = (event: SelectChangeEvent) => {
    setTimeRange(event.target.value as string);
  };

  const handleYearChange = (event: SelectChangeEvent) => {
    setYear(event.target.value as string);
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    const selectedMonth = event.target.value as string;
    console.log(`Month changed to: ${selectedMonth}`);
    setMonth(selectedMonth);
  };

  const handleDateRangeChange = (type: 'start' | 'end', date: Date | null) => {
    if (type === 'start') {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const handleChartTypeChange = (_event: React.MouseEvent<HTMLElement>, newChartType: string | null) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  // Get date range based on selected filters
  const getDateRange = (): { start: Date, end: Date } => {
    let start: Date, end: Date;
    
    if (timeRange === 'yearly') {
      // Yearly range: Jan 1 to Dec 31
      const yearNum = parseInt(year);
      start = new Date(yearNum, 0, 1); // Jan 1
      end = new Date(yearNum, 11, 31); // Dec 31
    } else if (timeRange === 'monthly') {
      // Monthly range: 1st of month to last day of month
      const yearNum = parseInt(year);
      const monthNum = parseInt(month); // Month is already stored as index (0-11)
      
      if (isNaN(monthNum) || monthNum < 0 || monthNum > 11) {
        console.error(`Invalid month index: ${month}`);
        // Default to January if month is invalid
        start = new Date(yearNum, 0, 1);
        end = new Date(yearNum, 0, 31);
      } else {
        start = new Date(yearNum, monthNum, 1); // 1st of month
        // Last day of month (by setting day 0 of next month)
        end = new Date(yearNum, monthNum + 1, 0);
      }
    } else if (timeRange === 'custom' && startDate && endDate) {
      // Custom date range
      start = startDate;
      end = endDate;
    } else {
      // Default to current year
      const currentYear = new Date().getFullYear();
      start = new Date(currentYear, 0, 1);
      end = new Date(currentYear, 11, 31);
    }
    
    // Ensure dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Invalid date range, using defaults');
      const currentYear = new Date().getFullYear();
      start = new Date(currentYear, 0, 1);
      end = new Date(currentYear, 11, 31);
    }
    
    return { start, end };
  };
  
  // Load trial balance data
  const loadTrialBalance = async () => {
    try {
      const { start, end } = getDateRange();
      console.log(`Loading trial balance for date range: ${start.toISOString()} to ${end.toISOString()}`);
      
      const data = await generateTrialBalance(start, end);
      setTrialBalanceData(data);
    } catch (error) {
      console.error('Error loading trial balance:', error);
      // Handle error
    }
  };

  // Load profit & loss data
  const loadProfitLoss = async () => {
    try {
      const { start, end } = getDateRange();
      console.log(`Loading profit & loss for date range: ${start.toISOString()} to ${end.toISOString()}`);

      const data = await generateProfitLoss(start, end);
      setProfitLossData(data);
    } catch (error) {
      console.error('Error loading profit & loss data:', error);
      // Handle error
    }
  };

  // Load balance sheet data
  const loadBalanceSheet = async () => {
    try {
      const { start, end } = getDateRange();
      console.log(`Loading balance sheet as of date: ${end.toISOString()}`);

      const data = await generateBalanceSheet(end, start);
      setBalanceSheetData(data);
    } catch (error) {
      console.error('Error loading balance sheet data:', error);
      // Handle error
    }
  };

  // Handle date range changes based on the global time range controls
  useEffect(() => {
    // Only update trial balance dates when on the trial balance tab
    if (tabValue === 6) {
      if (timeRange === 'yearly' && year) {
        // For yearly, set date range to the entire year
        setStartDate(new Date(`${year}-01-01`));
        setEndDate(new Date(`${year}-12-31`));
      } else if (timeRange === 'monthly' && year && month) {
        // For monthly, set date range to the specific month
        const monthIndex = months.indexOf(month);
        if (monthIndex !== -1) {
          const firstDay = new Date(parseInt(year), monthIndex, 1);
          const lastDay = new Date(parseInt(year), monthIndex + 1, 0);
          setStartDate(firstDay);
          setEndDate(lastDay);
        }
      } else if (timeRange === 'quarterly' && year) {
        // Handle quarterly if needed
        // This is a placeholder - implement based on your quarter selection logic
      }
      // For custom, we'll use the date pickers directly
    }
  }, [tabValue, timeRange, year, month]);
  
  // Initialize date range on first load
  useEffect(() => {
    // When the component first mounts, initialize the date range based on current year
    if (timeRange === 'yearly') {
      const currentYear = new Date().getFullYear();
      setStartDate(new Date(`${currentYear}-01-01`));
      setEndDate(new Date(`${currentYear}-12-31`));
    }
  }, []);
  
  // Check for tab query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 6) {
        setTabValue(tabIndex);
      }
    }
  }, [location]);
  
  // Load data based on active tab
  const loadActiveTabData = () => {
    if (tabValue === 6) { // Trial Balance tab (moved to index 6)
      loadTrialBalance();
    } else if (tabValue === 3) { // Profit & Loss tab
      loadProfitLoss();
    } else if (tabValue === 4) { // Balance Sheet tab
      loadBalanceSheet();
    }
  };

  // Load data when tab changes
  useEffect(() => {
    loadActiveTabData();
  }, [tabValue]);

  // Load data when time range or date changes
  useEffect(() => {
    loadActiveTabData();
  }, [timeRange, year, month, startDate, endDate]);

  // Include several years for selection
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => (currentYear - 2 + i).toString());
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Calculate totals
  const totalIncome = monthlyIncome.reduce((sum, month) => sum + month.amount, 0);
  const totalExpenses = monthlyExpenses.reduce((sum, month) => sum + month.amount, 0);
  const totalProfit = totalIncome - totalExpenses;
  const profitMargin = ((totalProfit / totalIncome) * 100).toFixed(1);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>Financial Reports</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ShareIcon />}
          >
            Share
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="report tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Income" {...a11yProps(1)} />
          <Tab label="Expenses" {...a11yProps(2)} />
          <Tab label="Profit & Loss" {...a11yProps(3)} />
          <Tab label="Balance Sheet" {...a11yProps(4)} />
          <Tab label="Taxes" {...a11yProps(5)} />
          <Tab label="Trial Balance" {...a11yProps(6)} icon={<AccountBalanceIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* Time range selector */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {timeRange === 'yearly' && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="year-label">Year</InputLabel>
              <Select
                labelId="year-label"
                value={year}
                label="Year"
                onChange={handleYearChange}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {timeRange === 'monthly' && (
            <>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel id="year-label">Year</InputLabel>
                <Select
                  labelId="year-label"
                  value={year}
                  label="Year"
                  onChange={handleYearChange}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="month-label">Month</InputLabel>
                <Select
                  labelId="month-label"
                  value={month}
                  label="Month"
                  onChange={handleMonthChange}
                >
                  {months.map((m, index) => (
                    <MenuItem key={m} value={index.toString()}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {timeRange === 'custom' && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="From"
                type="date"
                defaultValue="2023-01-01"
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="body2">to</Typography>
              <TextField
                size="small"
                label="To"
                type="date"
                defaultValue="2023-10-31"
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" size="small" startIcon={<CalendarIcon />}>
                Apply
              </Button>
            </Box>
          )}

          <Box sx={{ ml: 'auto' }}>
            <ToggleButtonGroup
              size="small"
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              aria-label="chart type"
            >
              <ToggleButton value="bar" aria-label="bar chart">
                Bar
              </ToggleButton>
              <ToggleButton value="line" aria-label="line chart">
                Line
              </ToggleButton>
              <ToggleButton value="pie" aria-label="pie chart">
                Pie
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Overview Tab Panel */}
        <TabPanel value={tabValue} index={0}>
          {/* Key metrics cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
            <Box>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Total Income</Typography>
                  <Typography variant="h4" color="primary" sx={{ my: 1, fontWeight: 600 }}>
                    ${totalIncome.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Revenue from all sources in {year}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Total Expenses</Typography>
                  <Typography variant="h4" color="error" sx={{ my: 1, fontWeight: 600 }}>
                    ${totalExpenses.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All expenses in {year}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Net Profit</Typography>
                  <Typography variant="h4" color="success.main" sx={{ my: 1, fontWeight: 600 }}>
                    ${totalProfit.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Profit margin: {profitMargin}%
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Charts/data visualization placeholders */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Income vs Expenses</Typography>
                  {/* Placeholder for chart visualization */}
                  <Box sx={{ height: 300, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Income vs Expenses Chart</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Monthly Profit Trend</Typography>
                  {/* Placeholder for chart visualization */}
                  <Box sx={{ height: 300, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Monthly Profit Trend Chart</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Income Breakdown</Typography>
                  {/* Placeholder for chart visualization */}
                  <Box sx={{ height: 300, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Income Categories Chart</Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {categoryBreakdown.map((item) => (
                      <Box key={item.category} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{item.category}</Typography>
                        <Typography variant="body2" fontWeight={500}>${item.amount.toLocaleString()} ({item.percentage}%)</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Expense Breakdown</Typography>
                  {/* Placeholder for chart visualization */}
                  <Box sx={{ height: 300, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Expense Categories Chart</Typography>
                  </Box>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {expensesBreakdown.map((item) => (
                      <Box key={item.category} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{item.category}</Typography>
                        <Typography variant="body2" fontWeight={500}>${item.amount.toLocaleString()} ({item.percentage}%)</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* Income Tab Panel */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Income Analysis</Typography>
              {/* Placeholder for income analysis */}
              <Box sx={{ height: 400, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Income Analysis Charts and Details</Typography>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Expenses Tab Panel */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Expense Analysis</Typography>
              {/* Placeholder for expense analysis */}
              <Box sx={{ height: 400, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Expense Analysis Charts and Details</Typography>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Profit & Loss Tab Panel */}
        <TabPanel value={tabValue} index={3}>
          <Card sx={{ 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {profitLossData ? (
              <>
                {/* Header with company info and document title */}
                <Box sx={{ 
                  p: 4, 
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    PROFIT & LOSS STATEMENT
                  </Typography>
                  
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {timeRange === 'yearly' ? `For the Year Ended ${months[11]} ${year}` :
                     timeRange === 'monthly' ? `For the Month Ended ${month} ${year}` :
                     timeRange === 'custom' && startDate && endDate ? 
                      `For the Period ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}` :
                      `For the Current Period`}
                  </Typography>
                  
                  <Divider sx={{ width: '40%', mx: 'auto', my: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary">
                    {`Generated on ${format(new Date(), 'dd MMM yyyy, h:mm a')}`}
                  </Typography>
                </Box>
                
                {/* Main content */}
                <Box sx={{ p: 4 }}>
                  {/* Note about using global time range */}
                  {timeRange === 'custom' && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">
                          For custom date ranges, please use the date selectors above.
                        </Typography>
                        <Button 
                          variant="outlined" 
                          onClick={loadProfitLoss}
                          size="small"
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Alert>
                  )}
                  
                  {/* Revenue Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      pb: 1, 
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      fontWeight: 600
                    }}>
                      REVENUE
                    </Typography>
                    
                    <Table size="small" sx={{ width: '100%' }}>
                      <TableBody>
                        {profitLossData.revenue.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 3, textAlign: 'center', border: 'none' }}>
                              <Typography variant="body2" color="text.secondary">
                                No revenue accounts found for the selected period.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          profitLossData.revenue.items.map((item: any, index: number) => (
                            <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: index === profitLossData.revenue.items.length - 1 ? 'none' : '1px solid rgba(224, 224, 224, 1)' } }}>
                              <TableCell sx={{ width: '20%', pl: 3, py: 1.5 }}>{item.accountNumber}</TableCell>
                              <TableCell sx={{ width: '50%', py: 1.5 }}>{item.accountName}</TableCell>
                              <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1.5 }}>
                                {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    
                    {/* Total Revenue Row */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pl: 3 }}>
                        Total Revenue
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pr: 3 }}>
                        {profitLossData.revenue.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* COGS Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      pb: 1, 
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.3),
                      fontWeight: 600
                    }}>
                      COST OF GOODS SOLD
                    </Typography>
                    
                    <Table size="small" sx={{ width: '100%' }}>
                      <TableBody>
                        {profitLossData.cogs.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 3, textAlign: 'center', border: 'none' }}>
                              <Typography variant="body2" color="text.secondary">
                                No COGS accounts found for the selected period.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          profitLossData.cogs.items.map((item: any, index: number) => (
                            <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: index === profitLossData.cogs.items.length - 1 ? 'none' : '1px solid rgba(224, 224, 224, 1)' } }}>
                              <TableCell sx={{ width: '20%', pl: 3, py: 1.5 }}>{item.accountNumber}</TableCell>
                              <TableCell sx={{ width: '50%', py: 1.5 }}>{item.accountName}</TableCell>
                              <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1.5 }}>
                                {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    
                    {/* Total COGS Row */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pl: 3 }}>
                        Total Cost of Goods Sold
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pr: 3 }}>
                        {profitLossData.cogs.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Gross Profit Section */}
                  <Box sx={{ mb: 5 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      py: 2,
                      px: 3,
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 1,
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        GROSS PROFIT
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {profitLossData.grossProfit.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Expenses Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      pb: 1, 
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.error.main, 0.3),
                      fontWeight: 600
                    }}>
                      EXPENSES
                    </Typography>
                    
                    <Table size="small" sx={{ width: '100%' }}>
                      <TableBody>
                        {profitLossData.expenses.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 3, textAlign: 'center', border: 'none' }}>
                              <Typography variant="body2" color="text.secondary">
                                No expense accounts found for the selected period.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          profitLossData.expenses.items.map((item: any, index: number) => (
                            <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: index === profitLossData.expenses.items.length - 1 ? 'none' : '1px solid rgba(224, 224, 224, 1)' } }}>
                              <TableCell sx={{ width: '20%', pl: 3, py: 1.5 }}>{item.accountNumber}</TableCell>
                              <TableCell sx={{ width: '50%', py: 1.5 }}>{item.accountName}</TableCell>
                              <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1.5 }}>
                                {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                    
                    {/* Total Expenses Row */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: alpha(theme.palette.error.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pl: 3 }}>
                        Total Expenses
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, pr: 3 }}>
                        {profitLossData.expenses.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Net Profit/Loss Section */}
                  <Box sx={{ 
                    mt: 5,
                    pt: 3,
                    borderTop: '2px double',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 2,
                      px: 3,
                      backgroundColor: profitLossData.netProfit >= 0 ? 
                        alpha(theme.palette.success.main, 0.1) : 
                        alpha(theme.palette.error.main, 0.1),
                      borderRadius: 1
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        NET {profitLossData.netProfit >= 0 ? 'PROFIT' : 'LOSS'}
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        color: profitLossData.netProfit >= 0 ? theme.palette.success.main : theme.palette.error.main
                      }}>
                        {profitLossData.netProfit.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                    
                    {/* Additional financial metrics */}
                    {profitLossData.revenue.total > 0 && (
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 4, pr: 3 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Profit Margin</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {((profitLossData.netProfit / profitLossData.revenue.total) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary">Gross Profit Margin</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {((profitLossData.grossProfit / profitLossData.revenue.total) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary">Expense Ratio</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {((profitLossData.expenses.total / profitLossData.revenue.total) * 100).toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Footer with notes */}
                  <Box sx={{ mt: 6, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      This statement has been prepared in accordance with generally accepted accounting principles.
                    </Typography>
                  </Box>
                </Box>
                
                {/* Action buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2, 
                  p: 2, 
                  borderTop: '1px solid', 
                  borderColor: 'divider',
                  backgroundColor: alpha(theme.palette.background.default, 0.5)
                }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<DownloadIcon />}
                  >
                    Export
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<PrintIcon />}
                  >
                    Print
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            )}
          </Card>
        </TabPanel>

        {/* Balance Sheet Tab Panel */}
        <TabPanel value={tabValue} index={4}>
          <Card sx={{
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {balanceSheetData ? (
              <>
                {/* Header with document title */}
                <Box sx={{
                  p: 4,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    BALANCE SHEET
                  </Typography>

                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {`As of ${format(balanceSheetData.asOfDate, 'dd MMM yyyy')}`}
                  </Typography>

                  <Divider sx={{ width: '40%', mx: 'auto', my: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    {`Generated on ${format(new Date(), 'dd MMM yyyy, h:mm a')}`}
                  </Typography>
                </Box>

                {/* Main content */}
                <Box sx={{ p: 4 }}>
                  {/* Note about using global time range */}
                  {timeRange === 'custom' && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">
                          For custom date ranges, please use the date selectors above.
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={loadBalanceSheet}
                          size="small"
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Alert>
                  )}

                  {/* ASSETS Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      fontWeight: 600
                    }}>
                      ASSETS
                    </Typography>

                    {/* Current Assets */}
                    {balanceSheetData.currentAssets.items.length > 0 && (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pl: 2 }}>
                          Current Assets
                        </Typography>
                        <Table size="small" sx={{ width: '100%', mb: 2 }}>
                          <TableBody>
                            {balanceSheetData.currentAssets.items.map((item: any, index: number) => (
                              <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: 'none' } }}>
                                <TableCell sx={{ width: '20%', pl: 4, py: 1 }}>{item.accountNumber}</TableCell>
                                <TableCell sx={{ width: '50%', py: 1 }}>{item.accountName}</TableCell>
                                <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1 }}>
                                  {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Total Current Assets */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                          pl: 4,
                          pr: 3,
                          py: 1,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5)
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Total Current Assets
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {balanceSheetData.currentAssets.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Non-Current Assets */}
                    {balanceSheetData.nonCurrentAssets.items.length > 0 && (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pl: 2, mt: 2 }}>
                          Non-Current Assets
                        </Typography>
                        <Table size="small" sx={{ width: '100%', mb: 2 }}>
                          <TableBody>
                            {balanceSheetData.nonCurrentAssets.items.map((item: any, index: number) => (
                              <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: 'none' } }}>
                                <TableCell sx={{ width: '20%', pl: 4, py: 1 }}>{item.accountNumber}</TableCell>
                                <TableCell sx={{ width: '50%', py: 1 }}>{item.accountName}</TableCell>
                                <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1 }}>
                                  {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Total Non-Current Assets */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                          pl: 4,
                          pr: 3,
                          py: 1,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5)
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Total Non-Current Assets
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {balanceSheetData.nonCurrentAssets.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Total Assets */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      pl: 3,
                      pr: 3,
                      borderTop: '2px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        TOTAL ASSETS
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {balanceSheetData.totalAssets.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* LIABILITIES Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.3),
                      fontWeight: 600
                    }}>
                      LIABILITIES
                    </Typography>

                    {/* Current Liabilities */}
                    {balanceSheetData.currentLiabilities.items.length > 0 && (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pl: 2 }}>
                          Current Liabilities
                        </Typography>
                        <Table size="small" sx={{ width: '100%', mb: 2 }}>
                          <TableBody>
                            {balanceSheetData.currentLiabilities.items.map((item: any, index: number) => (
                              <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: 'none' } }}>
                                <TableCell sx={{ width: '20%', pl: 4, py: 1 }}>{item.accountNumber}</TableCell>
                                <TableCell sx={{ width: '50%', py: 1 }}>{item.accountName}</TableCell>
                                <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1 }}>
                                  {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Total Current Liabilities */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                          pl: 4,
                          pr: 3,
                          py: 1,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5)
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Total Current Liabilities
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {balanceSheetData.currentLiabilities.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Non-Current Liabilities */}
                    {balanceSheetData.nonCurrentLiabilities.items.length > 0 && (
                      <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pl: 2, mt: 2 }}>
                          Non-Current Liabilities
                        </Typography>
                        <Table size="small" sx={{ width: '100%', mb: 2 }}>
                          <TableBody>
                            {balanceSheetData.nonCurrentLiabilities.items.map((item: any, index: number) => (
                              <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: 'none' } }}>
                                <TableCell sx={{ width: '20%', pl: 4, py: 1 }}>{item.accountNumber}</TableCell>
                                <TableCell sx={{ width: '50%', py: 1 }}>{item.accountName}</TableCell>
                                <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1 }}>
                                  {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        {/* Total Non-Current Liabilities */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 2,
                          pl: 4,
                          pr: 3,
                          py: 1,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5)
                        }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Total Non-Current Liabilities
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {balanceSheetData.nonCurrentLiabilities.total.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Total Liabilities */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      pl: 3,
                      pr: 3,
                      borderTop: '2px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        TOTAL LIABILITIES
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                        {balanceSheetData.totalLiabilities.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* EQUITY Section */}
                  <Box sx={{ mb: 5 }}>
                    <Typography variant="h6" sx={{
                      mb: 2,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: alpha(theme.palette.success.main, 0.3),
                      fontWeight: 600
                    }}>
                      EQUITY
                    </Typography>

                    <Table size="small" sx={{ width: '100%', mb: 2 }}>
                      <TableBody>
                        {/* Display equity accounts if any */}
                        {balanceSheetData.equity.items.length > 0 && balanceSheetData.equity.items.map((item: any, index: number) => (
                          <TableRow key={item.accountId} hover sx={{ '& td': { borderBottom: 'none' } }}>
                            <TableCell sx={{ width: '20%', pl: 3, py: 1 }}>{item.accountNumber}</TableCell>
                            <TableCell sx={{ width: '50%', py: 1 }}>{item.accountName}</TableCell>
                            <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1 }}>
                              {item.amount.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* Always show Retained Earnings (Net Income) */}
                        <TableRow hover sx={{
                          '& td': { borderBottom: 'none' },
                          backgroundColor: alpha(theme.palette.success.main, 0.05)
                        }}>
                          <TableCell sx={{ width: '20%', pl: 3, py: 1, fontWeight: 500 }}>-</TableCell>
                          <TableCell sx={{ width: '50%', py: 1, fontWeight: 500 }}>
                            Retained Earnings (Net Income)
                          </TableCell>
                          <TableCell align="right" sx={{ width: '30%', pr: 3, py: 1, fontWeight: 500 }}>
                            {balanceSheetData.retainedEarnings.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {/* Total Equity */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      pl: 3,
                      pr: 3,
                      borderTop: '2px solid',
                      borderColor: alpha(theme.palette.success.main, 0.5),
                      fontWeight: 'bold'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        TOTAL EQUITY
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                        {balanceSheetData.totalEquity.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Total Liabilities & Equity Section */}
                  <Box sx={{
                    mt: 5,
                    pt: 3,
                    borderTop: '2px double',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 2,
                      px: 3,
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 1
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        TOTAL LIABILITIES & EQUITY
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                        {balanceSheetData.totalLiabilitiesAndEquity.toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}
                      </Typography>
                    </Box>

                    {/* Balance check */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      {Math.abs(balanceSheetData.totalAssets - balanceSheetData.totalLiabilitiesAndEquity) < 0.01 ? (
                        <Chip
                          label="Balance Sheet is Balanced ✓"
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          label={`Out of Balance: ${Math.abs(balanceSheetData.totalAssets - balanceSheetData.totalLiabilitiesAndEquity).toLocaleString('en-MY', { style: 'currency', currency: 'MYR' })}`}
                          color="error"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Footer with notes */}
                  <Box sx={{ mt: 6, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      This balance sheet has been prepared in accordance with generally accepted accounting principles.
                    </Typography>
                  </Box>
                </Box>

                {/* Action buttons */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: alpha(theme.palette.background.default, 0.5)
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                  >
                    Export
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                  >
                    Print
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            )}
          </Card>
        </TabPanel>

        {/* Taxes Tab Panel */}
        <TabPanel value={tabValue} index={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tax Summary</Typography>
              {/* Placeholder for tax summary */}
              <Box sx={{ height: 400, bgcolor: '#f9f9f9', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">Tax Reports and Summaries</Typography>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Trial Balance Tab Panel */}
        <TabPanel value={tabValue} index={6}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ px: { xs: 2, sm: 3 } }}>
              {/* Note about using global time range */}
              {timeRange === 'custom' && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        For custom date ranges, please use the date selectors above.
                      </Typography>
                      <Button 
                        variant="outlined" 
                        onClick={loadTrialBalance}
                        size="small"
                      >
                        Refresh
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Title and Date Range */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Trial Balance
                </Typography>
                {trialBalanceData && trialBalanceData.startDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {`${format(new Date(trialBalanceData.startDate), 'dd MMM yyyy')} - ${format(new Date(trialBalanceData.endDate || new Date()), 'dd MMM yyyy')}`}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Search Box */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Empty box for alignment */}
                </Box>
                
                <TextField
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchQuery ? (
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }}
                  sx={{ width: { xs: '100%', sm: 250 }, ml: { xs: 0, sm: 2 } }}
                />
              </Box>
              
              {/* Trial Balance Table */}
              {trialBalanceData ? (
                <TableContainer component={Paper} variant="outlined" sx={{ 
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  overflow: 'auto',
                  width: '100%',
                  mt: 3
                }}>
                  <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        '& th': { 
                          color: theme.palette.primary.contrastText,
                          fontWeight: 'bold',
                          py: 2,
                          px: 2, // Add horizontal padding
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', pl: 3 }}>Account Number</TableCell>
                        <TableCell sx={{ width: '40%', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>Account Name</TableCell>
                        <TableCell align="right" sx={{ width: '20%', whiteSpace: 'nowrap' }}>Debit</TableCell>
                        <TableCell align="right" sx={{ width: '20%', whiteSpace: 'nowrap', pr: 3 }}>Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    
                    <TableBody>
                      {!trialBalanceData.items || trialBalanceData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center' }}>
                            <Typography variant="body1">
                              No accounts found.
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                              {searchQuery ? 'Try a different search term.' : 'There are no transactions in the selected period.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {trialBalanceData.items
                            .filter(item => {
                              if (!searchQuery) return true;
                              const query = searchQuery.toLowerCase();
                              return (
                                item.accountNumber.toLowerCase().includes(query) ||
                                item.accountName.toLowerCase().includes(query)
                              );
                            })
                            .map((item) => (
                            <TableRow 
                              key={item.accountId} 
                              sx={{ 
                                '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[100], 0.5) },
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05), cursor: 'pointer' }
                              }}
                              onClick={() => {
                                // Navigate to GL account transactions page with date range if available
                                if (trialBalanceData.startDate && trialBalanceData.endDate) {
                                  const startDateStr = format(new Date(trialBalanceData.startDate), 'yyyy-MM-dd');
                                  const endDateStr = format(new Date(trialBalanceData.endDate), 'yyyy-MM-dd');
                                  navigate(`/gl-account-transactions/${item.accountId}/${startDateStr}/${endDateStr}`);
                                } else {
                                  navigate(`/gl-account-transactions/${item.accountId}`);
                                }
                              }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap', pl: 3, py: 1.5 }}>
                                <Link 
                                  component="button" 
                                  variant="body2" 
                                  fontWeight="medium"
                                  underline="hover"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Navigate to GL account transactions page with date range if available
                                    if (trialBalanceData.startDate && trialBalanceData.endDate) {
                                      const startDateStr = format(new Date(trialBalanceData.startDate), 'yyyy-MM-dd');
                                      const endDateStr = format(new Date(trialBalanceData.endDate), 'yyyy-MM-dd');
                                      navigate(`/gl-account-transactions/${item.accountId}/${startDateStr}/${endDateStr}`);
                                    } else {
                                      navigate(`/gl-account-transactions/${item.accountId}`);
                                    }
                                  }}
                                >
                                  {item.accountNumber}
                                </Link>
                              </TableCell>
                              <TableCell sx={{ 
                                maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                py: 1.5
                              }}>
                                <Tooltip title={item.accountName} enterDelay={500}>
                                  <Link 
                                    component="button" 
                                    variant="body2"
                                    underline="hover"
                                    color="inherit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Navigate to GL account transactions page with date range if available
                                      if (trialBalanceData.startDate && trialBalanceData.endDate) {
                                        const startDateStr = format(new Date(trialBalanceData.startDate), 'yyyy-MM-dd');
                                        const endDateStr = format(new Date(trialBalanceData.endDate), 'yyyy-MM-dd');
                                        navigate(`/gl-account-transactions/${item.accountId}/${startDateStr}/${endDateStr}`);
                                      } else {
                                        navigate(`/gl-account-transactions/${item.accountId}`);
                                      }
                                    }}
                                  >
                                    {item.accountName}
                                  </Link>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 1.5 }}>
                                <Typography 
                                  variant="body2" 
                                  color={item.debitBalance > 0 ? 'success.main' : undefined}
                                  fontWeight={item.debitBalance > 0 ? 'medium' : undefined}
                                >
                                  {item.debitBalance > 0 ? formatCurrency(item.debitBalance) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 3, py: 1.5 }}>
                                <Typography 
                                  variant="body2" 
                                  color={item.creditBalance > 0 ? 'error.main' : undefined}
                                  fontWeight={item.creditBalance > 0 ? 'medium' : undefined}
                                >
                                  {item.creditBalance > 0 ? formatCurrency(item.creditBalance) : '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          
                          {/* Totals Row */}
                          {trialBalanceData.totalDebit !== undefined && (
                            <TableRow sx={{ 
                              backgroundColor: alpha(theme.palette.primary.light, 0.1),
                              fontWeight: 'bold',
                              '& td': { 
                                borderTop: `1px solid ${theme.palette.divider}`,
                                py: 2
                              }
                            }}>
                              <TableCell colSpan={2} sx={{ whiteSpace: 'nowrap', pl: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  TOTAL
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="subtitle2" color="success.main" fontWeight="bold">
                                  {formatCurrency(trialBalanceData.totalDebit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                                <Typography variant="subtitle2" color="error.main" fontWeight="bold">
                                  {formatCurrency(trialBalanceData.totalCredit)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          
                          {/* Balance Check Row */}
                          {trialBalanceData.totalDebit !== undefined && (
                            <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>
                              <TableCell colSpan={2} align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                  Difference
                                </Typography>
                              </TableCell>
                              <TableCell colSpan={2} align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                                <Typography 
                                  variant="body2" 
                                  color={Math.abs(trialBalanceData.totalDebit - trialBalanceData.totalCredit) < 0.01 
                                    ? 'success.main' 
                                    : 'error.main'
                                  }
                                  fontWeight="medium"
                                >
                                  {Math.abs(trialBalanceData.totalDebit - trialBalanceData.totalCredit) < 0.01
                                    ? 'Balanced ✓'
                                    : formatCurrency(Math.abs(trialBalanceData.totalDebit - trialBalanceData.totalCredit))
                                  }
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body1" color="text.secondary">
                    Click Apply to load the Trial Balance data.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Reports;
